import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import intl from "react-intl-universal";
import { Formik, Form } from "formik";
import { omit } from "lodash-es";
import { useMutate } from "restful-react";
import { useSnackbar } from "notistack";

import {
  ITeam,
  IWithballData,
  postWithballData,
  IPostWithballDataResponse,
} from "apis";
import { flipPosition } from "utils";

import { DataEditor } from "../DataEditor";
import { FIELD, POSSESSION_TYPE, EVENT_VALUES } from "../constants";
import { TEAM_TYPE, HOME_TEAM_SIDE, IStatsValue } from "../types";
import { getEventNameByValue } from "../utils";

type EditWithballDataDialogProps = {
  onClose: () => void;
  homeTeam?: ITeam;
  awayTeam?: ITeam;
  data: IWithballData | null;
  onDataUpdated: (data: IWithballData) => void;
};

export const EditWithballDataDialog = ({
  onClose,
  homeTeam,
  awayTeam,
  data,
  onDataUpdated,
}: EditWithballDataDialogProps) => {
  const { mutate: postData } = useMutate({
    verb: "POST",
    path: postWithballData(),
  });
  const { enqueueSnackbar } = useSnackbar();
  const initialValues = data
    ? {
        [FIELD.teamType]:
          data.teamid === homeTeam?.id ? TEAM_TYPE.HOME : TEAM_TYPE.AWAY,
        [FIELD.action]: getEventNameByValue(data.action),
        [FIELD.method]: data.method,
        [FIELD.bodyPart]: data.bodyPart,
        [FIELD.threat]: data.threat,
        [FIELD.succeed]: data.succeed,
        [FIELD.possession]: data.possession,
        [FIELD.homeTeamSide]: HOME_TEAM_SIDE.LEFT,
        [FIELD.fromPlayer]: data.fromPlayer,
        [FIELD.toPlayer]: data.toPlayer,
        [FIELD.createdAt]: data.created_at,
        [FIELD.teamId]: data.teamid,
        [FIELD.fromX]: data.fromX,
        [FIELD.fromY]: data.fromY,
        [FIELD.toX]: data.toX,
        [FIELD.toY]: data.toY,
      }
    : {
        [FIELD.teamType]: TEAM_TYPE.HOME,
        [FIELD.action]: "PASS",
        [FIELD.method]: 1,
        [FIELD.bodyPart]: 1,
        [FIELD.threat]: false,
        [FIELD.succeed]: true,
        [FIELD.possession]: POSSESSION_TYPE.keep,
        [FIELD.homeTeamSide]: HOME_TEAM_SIDE.LEFT,
        [FIELD.fromPlayer]: -1,
        [FIELD.toPlayer]: -1,
        [FIELD.createdAt]: 0,
      };
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, actions) => {
        const adaptedFromPosition = flipPosition(
          {
            x: values[FIELD.fromX] as number,
            y: values[FIELD.fromY] as number,
          },
          values[FIELD.teamType] === TEAM_TYPE.HOME,
          values[FIELD.homeTeamSide] === HOME_TEAM_SIDE.LEFT
        );
        const adaptedToPosition = flipPosition(
          {
            x: values[FIELD.toX] as number,
            y: values[FIELD.toY] as number,
          },
          values[FIELD.teamType] === TEAM_TYPE.HOME,
          values[FIELD.homeTeamSide] === HOME_TEAM_SIDE.LEFT
        );

        const input = {
          ...omit(values, [FIELD.teamType, FIELD.homeTeamSide, FIELD.action]),
          [FIELD.action]: EVENT_VALUES[values[FIELD.action] as IStatsValue],
          [FIELD.teamId]:
            values[FIELD.teamType] === TEAM_TYPE.HOME
              ? homeTeam?.id || -1
              : awayTeam?.id || -1,
          [FIELD.fromX]: adaptedFromPosition.x,
          [FIELD.fromY]: adaptedFromPosition.y,
          [FIELD.toX]: adaptedToPosition.x,
          [FIELD.toY]: adaptedToPosition.y,
          [FIELD.matchId]: data?.matchid,
          id: data?.id,
        };

        postData(input)
          .then((response: IPostWithballDataResponse) => {
            if (response.errCode > 0) {
              enqueueSnackbar(response.errMessage, {
                variant: "error",
              });
              return;
            }

            if (response.withballData) {
              onDataUpdated(response.withballData);
              enqueueSnackbar(
                intl.get("STAT_NOTIFICATION_WITHBALL_POST_SUCCESS"),
                {
                  variant: "success",
                  autoHideDuration: 1000,
                }
              );
            }
          })
          .catch(() => {
            enqueueSnackbar(
              intl.get("STAT_NOTIFICATION_WITHBALL_POST_FAILED"),
              {
                variant: "error",
              }
            );
          })
          .finally(() => {
            actions.setSubmitting(false);
          });
      }}
    >
      {({ submitForm, dirty, isSubmitting }) => {
        return (
          <Form>
            <Dialog open={!!data} onClose={onClose} maxWidth="md">
              <DialogTitle id="alert-dialog-title">
                {intl.get("EDIT_STATS_DIALOG_TITLE")}
              </DialogTitle>
              <DialogContent>
                <DataEditor homeTeam={homeTeam} awayTeam={awayTeam} />
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} color="primary">
                  {intl.get("EDIT_STATS_DIALOG_CANCEL")}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={!dirty || isSubmitting}
                  onClick={submitForm}
                >
                  {intl.get("EDIT_STATS_DIALOG_UPDATE")}
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};
